import { Box, Button, Divider, Flex, Heading, HStack, Image, SimpleGrid, Spinner, Stack, Tag, Text, VStack, IconButton } from '@chakra-ui/react';
import { FaWhatsapp, FaMapMarkerAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ContentErrorHandler from 'components/ContentErrorHandler';
import { BLUE, BLUE_DARKER } from 'constants/colors';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useGetUmkmDetailQuery } from './api';

export default function UmkmDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedProductImages, setSelectedProductImages] = useState<Record<number, number>>({});

  const { data: umkm, error, isLoading } = useGetUmkmDetailQuery(id!);

  const nextImage = () => {
    const images = umkm?.images;
    if (images && images.length > 0) {
      setSelectedImage((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    const images = umkm?.images;
    if (images && images.length > 0) {
      setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const nextProductImage = (productId: number, totalImages: number) => {
    setSelectedProductImages((prev) => ({
      ...prev,
      [productId]: ((prev[productId] ?? 0) + 1) % totalImages,
    }));
  };

  const prevProductImage = (productId: number, totalImages: number) => {
    setSelectedProductImages((prev) => ({
      ...prev,
      [productId]: ((prev[productId] ?? 0) - 1 + totalImages) % totalImages,
    }));
  };

  const formatPrice = (price?: number) => {
    if (!price) return '-';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <ContentErrorHandler isError={error} isLoading={isLoading}>
      <Box bg="white" minH="100vh" py={10}>
        <VStack spacing={8} maxW="1200px" mx="auto" px={4}>
          <Button
            alignSelf="flex-start"
            onClick={() => navigate(-1)}
            variant="outline"
            borderColor={BLUE}
            color={BLUE}
            _hover={{ bg: BLUE, color: 'white' }}
          >
            ← Kembali
          </Button>

          <Box w="100%" overflow="hidden" bg="white" borderRadius="md" shadow="md" p={6}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              {/* Image Gallery Section */}
              <Box>
                {umkm?.images && umkm.images.length > 0 ? (
                  <VStack spacing={4}>
                    <Box position="relative" w="100%">
                      <Image src={umkm.images[selectedImage]} alt={umkm.name} w="100%" h="400px" objectFit="cover" borderRadius="md" />
                      {umkm.images.length > 1 && (
                        <>
                          <IconButton
                            aria-label="Previous image"
                            icon={<FaChevronLeft />}
                            position="absolute"
                            left={2}
                            top="50%"
                            transform="translateY(-50%)"
                            onClick={prevImage}
                            bg={BLUE}
                            color="white"
                            rounded="full"
                            _hover={{ bg: BLUE_DARKER }}
                          />
                          <IconButton
                            aria-label="Next image"
                            icon={<FaChevronRight />}
                            position="absolute"
                            right={2}
                            top="50%"
                            transform="translateY(-50%)"
                            onClick={nextImage}
                            bg={BLUE}
                            color="white"
                            rounded="full"
                            _hover={{ bg: BLUE_DARKER }}
                          />
                          <Box
                            position="absolute"
                            bottom={2}
                            left="50%"
                            transform="translateX(-50%)"
                            bg="black"
                            color="white"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                          >
                            {selectedImage + 1} / {umkm.images.length}
                          </Box>
                        </>
                      )}
                    </Box>
                    {umkm.images.length > 1 && (
                      <HStack spacing={2} overflowX="auto" w="100%">
                        {umkm.images.map((img, idx) => (
                          <Image
                            key={idx}
                            src={img}
                            alt={`${umkm.name} ${idx + 1}`}
                            w="80px"
                            h="80px"
                            objectFit="cover"
                            borderRadius="md"
                            cursor="pointer"
                            border={selectedImage === idx ? `3px solid ${BLUE}` : 'none'}
                            onClick={() => setSelectedImage(idx)}
                          />
                        ))}
                      </HStack>
                    )}
                  </VStack>
                ) : (
                  <Box w="100%" h="400px" bg="gray.200" display="flex" alignItems="center" justifyContent="center" borderRadius="md">
                    <Text color="gray.500">Tidak ada gambar</Text>
                  </Box>
                )}
              </Box>

              {/* Info Section */}
              <VStack align="stretch" spacing={4}>
                <Heading color={BLUE_DARKER} size="xl">
                  {umkm?.name}
                </Heading>

                {umkm?.owner && (
                  <Text fontSize="lg" color="gray.600">
                    <strong>Pemilik:</strong> {umkm.owner.name}
                  </Text>
                )}

                <HStack spacing={2} flexWrap="wrap">
                  {umkm?.categories?.map((cat) => (
                    <Tag key={cat.id} size="md" bg={BLUE} color="white">
                      {cat.name}
                    </Tag>
                  ))}
                </HStack>

                <Divider />

                {umkm?.description && (
                  <Box>
                    <Heading size="sm" mb={2} color={BLUE}>
                      Deskripsi
                    </Heading>
                    <Text whiteSpace="pre-wrap">{umkm.description}</Text>
                  </Box>
                )}

                {umkm?.address && (
                  <HStack align="flex-start" spacing={2}>
                    <FaMapMarkerAlt color={BLUE} size={20} />
                    <Box flex={1}>
                      <Heading size="sm" mb={1} color={BLUE}>
                        Alamat
                      </Heading>
                      <Text>{umkm.address}</Text>
                    </Box>
                  </HStack>
                )}

                {umkm?.whatsappNumber && (
                  <Button
                    leftIcon={<FaWhatsapp />}
                    bg={BLUE}
                    color="white"
                    size="lg"
                    w="100%"
                    _hover={{ bg: BLUE_DARKER }}
                    onClick={() => window.open(`https://wa.me/${umkm.whatsappNumber}`, '_blank')}
                  >
                    Hubungi via WhatsApp
                  </Button>
                )}
              </VStack>
            </SimpleGrid>

            {/* Products Section */}
            {umkm?.products && umkm.products.length > 0 && (
              <Box mt={8}>
                <Heading size="lg" mb={6} color={BLUE_DARKER}>
                  Produk yang Tersedia
                </Heading>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
                  {umkm.products.map((product) => {
                    const productImages = product.images && product.images.length > 0 ? product.images : [];
                    const currentImageIdx = selectedProductImages[product.id] || 0;
                    const currentImage = productImages[currentImageIdx];

                    return (
                      <Box key={product.id} bg="white" borderRadius="md" shadow="sm" p={3}>
                        <Stack spacing={2}>
                          {currentImage && (
                            <Box position="relative" w="100%">
                              <Image src={currentImage} alt={product.name} h="150px" w="100%" objectFit="cover" borderRadius="md" />
                              {productImages.length > 1 && (
                                <>
                                  <IconButton
                                    aria-label="Previous product image"
                                    icon={<FaChevronLeft />}
                                    position="absolute"
                                    left={1}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={() => prevProductImage(product.id, productImages.length)}
                                    bg={BLUE}
                                    color="white"
                                    size="sm"
                                    rounded="full"
                                    _hover={{ bg: BLUE_DARKER }}
                                  />
                                  <IconButton
                                    aria-label="Next product image"
                                    icon={<FaChevronRight />}
                                    position="absolute"
                                    right={1}
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={() => nextProductImage(product.id, productImages.length)}
                                    bg={BLUE}
                                    color="white"
                                    size="sm"
                                    rounded="full"
                                    _hover={{ bg: BLUE_DARKER }}
                                  />
                                  <Box
                                    position="absolute"
                                    bottom={1}
                                    right={1}
                                    bg="black"
                                    color="white"
                                    px={2}
                                    py={0.5}
                                    borderRadius="full"
                                    fontSize="xs"
                                  >
                                    {currentImageIdx + 1} / {productImages.length}
                                  </Box>
                                </>
                              )}
                            </Box>
                          )}
                          <Heading size="sm" color={BLUE}>
                            {product.name}
                          </Heading>
                          {product.price && (
                            <Text fontWeight="bold" color="green.500" fontSize="lg">
                              {formatPrice(product.price)}
                            </Text>
                          )}
                          {product.description && (
                            <Text fontSize="sm" color="gray.600" noOfLines={3}>
                              {product.description}
                            </Text>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}
          </Box>
        </VStack>
      </Box>
    </ContentErrorHandler>
  );
}
